/* global Theme -- defined by snippets/scripts.liquid */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Adds lines via the Ajax API and dispatches a CartLinesUpdateEvent from `source`,
 * so Horizon's cart icon, cart items and drawer update themselves (they treat it
 * as an external update and refetch their section).
 * @param {HTMLElement} source - element the event is dispatched from
 * @param {Array<{id: number, quantity: number}>} items
 * @param {string} sourceName - identifies the caller in the event detail
 */
async function addLines(source, items, sourceName) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const added = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: sourceName, didError: Boolean(added.status) },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/** @param {HTMLButtonElement | null} button @param {boolean} busy */
function setBusy(button, busy) {
  if (!button) return;
  button.toggleAttribute('disabled', busy);
  if (busy) button.setAttribute('aria-busy', 'true');
  else button.removeAttribute('aria-busy');
}

/**
 * Progressive enhancement for a plain /cart/add form (one variant, quantity 1).
 * Without JavaScript the form posts normally.
 */
class BootbibAddButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    // Listen on the element, not the form: the cart drawer morphs its content and may replace the form.
    this.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const variantId = Number(new FormData(form).get('id'));
    if (!variantId) return;
    const button = form.querySelector('button');
    setBusy(button, true);
    await addLines(this, [{ id: variantId, quantity: 1 }], 'bootbib-add-button');
    setBusy(button, false);
  };
}

/**
 * "Buy more, save more" ladder (blocks/bootbib-quantity-ladder.liquid).
 * Choosing 2 or 3 reveals selects for the extra pieces; totals follow the
 * selection with the ladder discount, rounded per unit like Shopify does (discount down).
 */
class BootbibLadder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('change', this.#render, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.querySelector('[data-sticky-submit]')?.addEventListener(
      'click',
      () => this.querySelector('form')?.requestSubmit(),
      { signal }
    );
    // The sticky bar (mobile only, via CSS) shows while the ladder's own button is out of view.
    const sticky = this.querySelector('[data-sticky]');
    const submit = this.querySelector('.ladder__submit');
    if (sticky instanceof HTMLElement && submit) {
      this.#observer = new IntersectionObserver(([entry]) => {
        sticky.hidden = Boolean(entry?.isIntersecting);
      });
      this.#observer.observe(submit);
    }
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  get #quantity() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? Number(checked.value) : 1;
  }

  /** @returns {number} discount percent for a total quantity */
  #percentFor(quantity) {
    const [tier2, tier3] = (this.dataset.tiers ?? '0,0').split(',').map(Number);
    if (quantity >= 3) return tier3 ?? 0;
    if (quantity === 2) return tier2 ?? 0;
    return 0;
  }

  /**
   * Selected lines for a quantity: the page's product plus chosen extras, merged per variant.
   * @param {number} quantity
   * @returns {Map<number, {quantity: number, price: number}>}
   */
  #lines(quantity) {
    const primary = this.querySelector('[data-primary]');
    const submit = this.querySelector('[data-price-primary]');
    /** @type {Map<number, {quantity: number, price: number}>} */
    const lines = new Map();
    const add = (/** @type {number} */ id, /** @type {number} */ price) => {
      const line = lines.get(id) ?? { quantity: 0, price };
      line.quantity += 1;
      lines.set(id, line);
    };
    if (primary instanceof HTMLInputElement && submit instanceof HTMLElement) {
      add(Number(primary.value), Number(submit.dataset.pricePrimary));
    }
    for (let n = 2; n <= quantity; n++) {
      const select = this.querySelector(`[data-slot="${n}"] select`);
      const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
      if (option) add(Number(option.value), Number(option.dataset.price));
    }
    return lines;
  }

  /** @param {number} quantity */
  #total(quantity) {
    const pct = this.#percentFor(quantity);
    let total = 0;
    for (const { quantity: qty, price } of this.#lines(quantity).values()) {
      // Shopify rounds the discount per unit down (the unit price up), then multiplies by quantity.
      total += Math.floor((price * (100 - pct) + 99) / 100) * qty;
    }
    return total;
  }

  #format(/** @type {number} */ cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'EUR');
  }

  #render = () => {
    const quantity = this.#quantity;
    const threshold = Number(this.dataset.threshold);
    const slots = this.querySelector('[data-slots]');
    if (slots instanceof HTMLElement) slots.hidden = quantity < 2;
    for (const slot of this.querySelectorAll('[data-slot]')) {
      if (slot instanceof HTMLElement) slot.hidden = Number(slot.dataset.slot) > quantity;
    }

    // Each option shows its total for the current selection of extra pieces.
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const option = radio.closest('label');
      const n = Number(radio.value);
      const total = this.#total(n);
      const before = [...this.#lines(n).values()].reduce((sum, l) => sum + l.price * l.quantity, 0);
      const totalEl = option?.querySelector('[data-total]');
      const beforeEl = option?.querySelector('[data-before]');
      const shipping = option?.querySelector('[data-shipping-tag]');
      if (totalEl) totalEl.textContent = this.#format(total);
      if (beforeEl) beforeEl.textContent = this.#format(before);
      // Free shipping from the threshold, or from data-free-items items (2 outside the US).
      if (shipping instanceof HTMLElement) shipping.hidden = n < Number(this.dataset.freeItems ?? 2) && total < threshold;
    }

    const total = this.#format(this.#total(quantity));
    for (const el of this.querySelectorAll('[data-submit-total], [data-sticky-total]')) el.textContent = total;
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const items = [...this.#lines(this.#quantity).entries()].map(([id, { quantity }]) => ({ id, quantity }));
    const buttons = [...this.querySelectorAll('button[type="submit"], [data-sticky-submit]')].filter(
      (b) => b instanceof HTMLButtonElement
    );
    for (const b of buttons) setBusy(/** @type {HTMLButtonElement} */ (b), true);
    await addLines(this, items, 'bootbib-ladder');
    for (const b of buttons) setBusy(/** @type {HTMLButtonElement} */ (b), false);
  };
}

/**
 * Free-shipping progress. Renders server-side; after any cart change it reads
 * /cart.js and updates the bar and message, formatting money like Liquid's `money`.
 */
class BootbibFreeShipping extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = async (event) => {
    try {
      await event.promise;
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      this.update(cart.total_price, cart.currency, cart.item_count);
    } catch {
      // A failed cart update leaves the server-rendered state in place.
    }
  };

  /**
   * @param {number} total - cart total in minor units
   * @param {string} currency
   * @param {number} [itemCount] - data-free-items or more items always ship free (0 = no item rule)
   */
  update(total, currency, itemCount = 0) {
    const threshold = Number(this.dataset.threshold);
    const freeItems = Number(this.dataset.freeItems ?? 2);
    const remaining = freeItems > 0 && itemCount >= freeItems ? 0 : Math.max(threshold - total, 0);
    const bar = this.querySelector('[data-bar]');
    const message = this.querySelector('[data-message]');
    if (bar instanceof HTMLElement) bar.style.inlineSize = `${remaining === 0 ? 100 : Math.min((total / threshold) * 100, 100)}%`;
    if (!message) return;

    if (remaining === 0) {
      message.textContent = this.dataset.reached ?? '';
      return;
    }
    const amount = formatMoney(remaining, this.dataset.moneyFormat ?? '{{amount}}', currency);
    message.innerHTML = (this.dataset.template ?? '').replace('[amount]', amount);
  }
}

if (!customElements.get('bootbib-add-button')) customElements.define('bootbib-add-button', BootbibAddButton);
if (!customElements.get('bootbib-ladder')) customElements.define('bootbib-ladder', BootbibLadder);
if (!customElements.get('bootbib-free-shipping')) customElements.define('bootbib-free-shipping', BootbibFreeShipping);
